import InterviewNotificationChoice from "../InterviewNotificationChoice";
import { freshModal } from "../freshModal";
import React, { useEffect, useRef, useState } from "react";
import {Modal,Select} from "antd";
import {Calendar,User,Building2,Clock,MapPin,Loader2} from "lucide-react";
import { previousBusinessDay } from "../../utils/interviewReminder";
import qualifications from "../../utils/qualifications";
import axios from "../../axios/AxiosInstance";
import { toast } from "react-toastify";

const clientAddresses = (client) =>
  [
    ["", "Siège"],
    ["Livraison", "Livraison"],
    ["Facturation", "Facturation"],
  ].flatMap(([suffix, label]) => {
    const street = String(client?.[`address${suffix}`] || "").trim();
    if (!street) return [];
    const city = [client?.[`postCode${suffix}`], client?.[`town${suffix}`]]
      .filter(Boolean)
      .join(" ");
    return [{ label, value: [street, city].filter(Boolean).join(", ") }];
  });

const AddInterviewModalFormSession = ({
  isOpen,
  onClose,
  candidate,
  onSubmit,
  initialClient,
}) => {
  const [formData, setFormData] = useState({
    date: "",
    hour: "",
    client: initialClient?._id || "",
    type: "présentiel",
    comment: "",
    place: clientAddresses(initialClient)[0]?.value || "",
    candidate: candidate._id,
  });

  const [notificationId,setNotificationId]=useState(null);
  const [busy, setBusy] = useState(false);
  const [createdInterview, setCreatedInterview] = useState(null);
  const [selectedClient, setSelectedClient] = useState(initialClient || null);
  const addresses = clientAddresses(selectedClient);
  const [clients, setClients] = useState([]);
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [clientSearch, setClientSearch] = useState(
    initialClient?.companyName || "",
  );
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const placeRef = useRef(null);
  const timerRef = useRef(null); // Use useRef to hold the debounce timer

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  // Handle client selection
  const handleClientSelection = (client) => {
    setSelectedClient(client);
    setFormData({
      ...formData,
      client: client._id,
      place: clientAddresses(client)[0]?.value || "",
    });
    setClientSearch(client.companyName); // set the search text to the selected client name
    setShowClientDropdown(false); // hide the dropdown after selection
  };

  // Search clients with debouncing
  const searchClient = (word) => {

    setClientSearch(word);
    setSelectedClient(null);
    setFormData((current) => ({ ...current, client: "", place: "" }));
    if (word.length === 0) {
      setClients([]);
    }

    // Do not trigger search if word is too short
    if (word.length < 3) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // Clear the previous timer and create a new one
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setIsLoadingClient(true);
      axios
        .post("client/freelance/find-by-text", { query: word })
        .then((res) => {
          setClients(res.data);
          setShowClientDropdown(true);
        })
        .catch((e) => console.log(e))
        .finally(() => setIsLoadingClient(false));
    }, 700); // 700ms debounce time
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!formData.client) {
      toast.error("Sélectionnez un client dans les résultats.");
      return;
    }
    const userId = localStorage.getItem("userId");
    if (!userId) {
      toast.error("Reconnectez-vous pour créer le rappel.");
      return;
    }
    setBusy(true);
    let saved = createdInterview;
    try {
      const reminderDate = previousBusinessDay(formData.date);
      if (!saved) {
        const { data } = await axios.post("entretien", formData);
        saved = data || { ...formData };
        setCreatedInterview(saved);
      }
      await axios.post("newtask", {
        type: "Appel",
        priority: "Haute",
        date: reminderDate,
        hour: "09:00",
        comment: `Confirmer l’entretien du ${formData.date} à ${formData.hour} avec ${candidate.firstname || ""} ${candidate.lastname || ""} et ${clientSearch}. ${formData.place || ""} ${formData.comment || ""}`,
        clients: [formData.client],
        candidates: [candidate._id],
        taskCreator: userId,
        taskManagers: [userId],
      });
      toast.success("Entretien et rappel créés.");
      if(saved?._id)setNotificationId(saved._id);
      else {onSubmit?.(saved || formData);onClose();}
    } catch {
      toast.error(
        saved
          ? "Entretien enregistré, mais rappel non créé. Réessayez pour créer uniquement le rappel."
          : "Impossible d’enregistrer l’entretien.",
      );
    } finally {
      setBusy(false);
    }
  };

  const fieldClass="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 focus:border-slate-400 focus:outline-none";
  const labelClass="mb-2 block text-sm font-medium text-slate-600";
  return (
    <><Modal open={isOpen&&!notificationId} onCancel={()=>{if(!busy)onClose();}} centered width={760} zIndex={2200} footer={null} styles={{body:{maxHeight:'75vh',overflowY:'auto',paddingRight:4}}} maskClosable={!busy} keyboard={!busy} closable={!busy}
      title={<div className="flex items-center gap-3 pr-8"><span className="rounded-xl bg-slate-100 p-3 text-slate-600"><Calendar size={22}/></span><div><h2 className="text-xl font-semibold text-slate-900">Planifier un entretien</h2><p className="mt-1 text-sm font-normal text-slate-500">Organisez le rendez-vous et son rappel de confirmation.</p></div></div>}>
      <form onSubmit={handleSubmit} className="mt-6 space-y-5">
        <fieldset disabled={busy||Boolean(createdInterview)} className="space-y-5">
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start gap-3"><User size={18} className="mt-0.5 shrink-0 text-slate-400"/><div><p className="text-xs text-slate-500">Candidat</p><p className="mt-1 text-sm font-semibold text-slate-800">{[candidate.firstname,candidate.lastname].filter(Boolean).join(' ')||'Candidat sélectionné'}</p></div></div>
            {initialClient?<div className="mt-4 flex items-start gap-3 border-t border-slate-200 pt-4"><Building2 size={18} className="mt-0.5 shrink-0 text-slate-400"/><div><p className="text-xs text-slate-500">Client</p><p className="mt-1 text-sm font-semibold text-slate-800">{initialClient.companyName}</p></div></div>:<div className="relative mt-4 border-t border-slate-200 pt-4"><label htmlFor="interview-client-search" className={labelClass}>Client concerné</label><input id="interview-client-search" value={clientSearch} onChange={event=>searchClient(event.target.value)} placeholder="Rechercher le client (3 caractères minimum)" className={fieldClass}/>{isLoadingClient&&<p className="mt-2 text-xs text-slate-400">Recherche des clients…</p>}{showClientDropdown&&clients.length>0&&<div className="mt-2 max-h-48 overflow-auto rounded-lg border border-slate-200 bg-white">{clients.map(client=><button key={client._id} type="button" onClick={()=>handleClientSelection(client)} className="block w-full border-b border-slate-100 px-3 py-3 text-left text-sm text-slate-700 hover:bg-slate-50">{client.companyName}</button>)}</div>}{formData.client&&<p className="mt-2 text-xs text-slate-500">Client sélectionné : {clientSearch}</p>}</div>}
          </section>
          <section className="rounded-xl border border-slate-200 p-4 sm:p-5">
            <h3 className="mb-4 text-sm font-semibold text-slate-900">Date et modalités</h3>
            <div className="grid gap-4 sm:grid-cols-2"><div><label htmlFor="date" className={labelClass}>Date</label><input lang="fr-FR" id="date" type="date" name="date" required value={formData.date} onChange={handleChange} className={fieldClass}/></div><div><label htmlFor="hour" className={labelClass}>Heure</label><input lang="fr-FR" id="hour" type="time" name="hour" required value={formData.hour} onChange={handleChange} className={fieldClass}/></div></div>
            <div className="mt-4"><label htmlFor="type" className={labelClass}>Type d'entretien</label><select id="type" name="type" value={formData.type} onChange={handleChange} className={fieldClass}><option value="présentiel">Présentiel</option><option value="téléphonique">Téléphonique</option><option value="visioconférence">Visioconférence</option></select></div>
            <div className="mt-4"><label htmlFor="interview-position" className={labelClass}>Poste concerné</label><Select id="interview-position" aria-label="Poste concerné" showSearch allowClear optionFilterProp="label" placeholder="Choisir un poste" value={formData.poste||undefined} onChange={poste=>setFormData(current=>({...current,poste:poste||""}))} options={qualifications.map(value=>({value,label:value}))} className="w-full" size="large" disabled={busy||Boolean(createdInterview)}/></div>
          </section>
          <section className="rounded-xl border border-slate-200 p-4 sm:p-5">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-900"><MapPin size={16} className="text-slate-400"/>Lieu et informations utiles</h3>
            {addresses.length>0&&<div className="mb-4"><label htmlFor="client-address" className={labelClass}>Adresses enregistrées du client</label><select id="client-address" value={addresses.some(address=>address.value===formData.place)?formData.place:''} onChange={event=>setFormData(current=>({...current,place:event.target.value}))} className={fieldClass}><option value="">Autre adresse / lieu personnalisé</option>{addresses.map(address=><option key={address.label} value={address.value}>{address.label} · {address.value}</option>)}</select></div>}
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2"><label htmlFor="place" className="text-sm font-medium text-slate-600">Lieu</label><button type="button" onClick={()=>{setFormData(current=>({...current,place:''}));placeRef.current?.focus();}} className="text-xs font-medium text-slate-600 underline">Saisir une autre adresse</button></div><input ref={placeRef} type="text" id="place" name="place" placeholder="Adresse, numéro à appeler ou lien de visioconférence" value={formData.place} onChange={handleChange} className={fieldClass}/><p className="mt-2 text-xs text-slate-400">Ce lieu concerne uniquement l’entretien. L’adresse de la fiche client reste inchangée.</p>
            <label htmlFor="comment" className={`${labelClass} mt-4`}>Notes complémentaires</label><textarea id="comment" name="comment" rows={3} value={formData.comment} onChange={handleChange} placeholder="Contact à rencontrer, accès, documents à prévoir…" className={`${fieldClass} resize-y`}/>
          </section>
        </fieldset>
        <div className="flex items-start gap-3 rounded-lg bg-slate-50 p-3 text-xs leading-5 text-slate-500"><Clock size={16} className="mt-0.5 shrink-0"/><p>Un rappel sera ajouté à Ma journée à 9 h la veille ouvrée du rendez-vous (hors week-ends et jours fériés français).</p></div>
        {createdInterview&&<p role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">L’entretien est enregistré. Il reste à créer le rappel : vous pouvez réessayer sans créer de doublon.</p>}
        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 pt-5"><button type="button" onClick={onClose} disabled={busy} className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-600 disabled:opacity-40">Annuler</button><button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-40">{busy&&<Loader2 size={16} className="animate-spin"/>}{busy?'Enregistrement…':createdInterview?'Réessayer le rappel':'Confirmer et créer le rappel'}</button></div>
      </form>
    </Modal>{notificationId&&<InterviewNotificationChoice interviewId={notificationId} onDone={()=>{onSubmit?.(createdInterview||formData);onClose();}}/>}</>
  );
};

export const AddInterviewModal = freshModal(
  AddInterviewModalFormSession,
  "isOpen",
);
